/**
 * The snake_case ↔ camelCase boundary mapping — conventions.md §2.3.
 *
 * Over-the-wire JSON is snake_case; TS-side fields are camelCase. This module is THE ONE
 * PLACE that mapping happens (conventions.md §2.3 — "the boundary mapping ... lives in the
 * REST client layer"). It also hosts the embedding-free guard (conventions.md §6).
 *
 * Design choice — a generic deep recursive `keysToCamel` / `keysToSnake` rather than a
 * hand-written mapper per type. Rationale: the backend wire shapes are large and the field
 * names are mechanically related to the TS names (pure snake↔camel, no renames). A generic
 * walker is far less error-prone than ~60 hand mappers that must each be kept in sync, and
 * the typed `*View` interfaces in `src/types/` give us the compile-time shape guarantee a
 * hand mapper would. The walker is structure-only — it never inspects values — except for
 * the embedding-free assertion, which is a deliberate exception (a hard, tested contract).
 *
 * THE EMBEDDING-FREE GUARD (conventions.md §6 / frontend.md §2.5): `assertNoEmbedding`
 * walks an inbound payload and, if it finds any key named `embedding` / `vector` /
 * `centroid`, logs at ERROR and throws. The typed `*View` shapes already make embeddings
 * structurally absent; this is the runtime backstop the design doc mandates ("logs+errors
 * on any payload carrying an `embedding` field").
 */

import { getLogger } from './logger';

const log = getLogger('api.mappers');

// ---------------------------------------------------------------------------
// The embedding-free guard (conventions.md §6)
// ---------------------------------------------------------------------------

/** Field names that must never cross the wire into the frontend (conventions.md §6). */
const FORBIDDEN_VECTOR_KEYS = new Set(['embedding', 'vector', 'centroid', 'halfvec']);

/**
 * Walk a parsed JSON payload; throw if any object key is a raw-vector field name.
 *
 * This is the hard contract from conventions.md §6 — the backend's `to_wire()` strips
 * these, and the typed `*View` shapes make them structurally impossible, but a contract
 * regression on the backend must fail loudly on the client, not silently bloat the laptop
 * budget. Called by `client.ts` on every inbound payload before mapping.
 */
export function assertNoEmbedding(value: unknown, path = '$'): void {
  if (value === null || typeof value !== 'object') return;
  if (Array.isArray(value)) {
    value.forEach((v, i) => assertNoEmbedding(v, `${path}[${i}]`));
    return;
  }
  for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
    if (FORBIDDEN_VECTOR_KEYS.has(key.toLowerCase())) {
      log.error('embedding_on_wire', { path, key });
      throw new Error(
        `Embedding-free wire rule violated (conventions.md §6): a payload at ${path} ` +
          `carries a forbidden '${key}' field. The backend's to_wire() boundary must ` +
          `project it out.`,
      );
    }
    assertNoEmbedding(child, `${path}.${key}`);
  }
}

// ---------------------------------------------------------------------------
// snake_case ↔ camelCase
// ---------------------------------------------------------------------------

const SNAKE_RE = /_([a-z0-9])/g;
const CAMEL_RE = /([A-Z])/g;

function snakeKeyToCamel(key: string): string {
  return key.replace(SNAKE_RE, (_m, c: string) => c.toUpperCase());
}

function camelKeyToSnake(key: string): string {
  return key.replace(CAMEL_RE, (m) => `_${m.toLowerCase()}`);
}

/**
 * Recursively rewrite every object key from snake_case to camelCase. Values are passed
 * through untouched — strings (including ISO timestamps and id strings), numbers, bools,
 * and nulls are never inspected. Arrays are walked element-wise.
 */
export function keysToCamel<T = unknown>(value: unknown): T {
  if (value === null || typeof value !== 'object') return value as T;
  if (Array.isArray(value)) return value.map((v) => keysToCamel(v)) as unknown as T;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    out[snakeKeyToCamel(k)] = keysToCamel(v);
  }
  return out as T;
}

/**
 * Recursively rewrite every object key from camelCase to snake_case — the outbound
 * direction, used on request bodies. `undefined`-valued keys are dropped (the backend
 * request models use `extra="forbid"` but treat an absent optional field as its default;
 * sending an explicit `null` would be a different signal, so we only drop `undefined`).
 */
export function keysToSnake<T = unknown>(value: unknown): T {
  if (value === null || typeof value !== 'object') return value as T;
  if (Array.isArray(value)) return value.map((v) => keysToSnake(v)) as unknown as T;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (v === undefined) continue;
    out[camelKeyToSnake(k)] = keysToSnake(v);
  }
  return out as T;
}
